import { useMemo } from "react";
import { useSWRConfig } from "swr";
import useResources from "./use-resources";
import useProfileFilter from "./use-profile-filter";
import { findAndUpdate } from "../utils/index.jsx";
import { naturalCompare } from "../utils/sort";

const ROOT_PATH = "property-folders";

export default function usePropertyFolders(options = {}) {
  const { withById = false } = options;
  const {
    resources: allPropertyFolders,
    resourcesById: allPropertyFoldersById,
    error,
    isLoading,
    update,
    del,
    create,
    axios,
    mutate,
  } = useResources(ROOT_PATH, {}, { withById });
  const { mutate: globalMutate } = useSWRConfig();

  const { matchesProfileFilter, isFilterActive } = useProfileFilter();

  const propertyFolders = useMemo(() => {
    const filtered = isFilterActive
      ? allPropertyFolders.filter((folder) =>
          matchesProfileFilter(folder.id_profile_nom_owner),
        )
      : [...allPropertyFolders];
    return filtered.sort((f1, f2) => naturalCompare(f1.nom, f2.nom));
  }, [allPropertyFolders, isFilterActive, matchesProfileFilter]);

  const propertyFoldersById = useMemo(() => {
    if (!withById) return allPropertyFoldersById;
    if (!isFilterActive) return allPropertyFoldersById;
    return propertyFolders.reduce((acc, folder) => {
      acc[folder.id] = folder;
      return acc;
    }, {});
  }, [propertyFolders, allPropertyFoldersById, withById, isFilterActive]);

  // Le back propage le statut d'un dossier à ses biens et sous-dossiers : la
  // liste des biens, qui vit sur sa propre clé SWR, doit être rechargée.
  async function updatePropertyFolder(propertyFolderId, resource, options) {
    const updated = await update(propertyFolderId, resource, options);
    if ("status" in resource) {
      await globalMutate((key) => Array.isArray(key) && key[0] === "biens");
    }
    return updated;
  }

  async function propertyFolderToQlower(propertyFolderId) {
    let newResource;
    await mutate(
      async (current) => {
        const response = await axios.put(
          `${ROOT_PATH}/${propertyFolderId}/send-to-compta`,
        );
        newResource = response.data;
        return findAndUpdate(
          current,
          (resource) => resource.id === propertyFolderId,
          newResource,
        );
      },
      { revalidate: false },
    );
    return newResource;
  }

  async function propertyFolderTransacToQlower(propertyFolderId, year) {
    let newResource;
    await mutate(
      async (current) => {
        const response = await axios.put(
          `${ROOT_PATH}/${propertyFolderId}/send-transac-to-compta`,
          { year },
        );
        newResource = response.data;
        return findAndUpdate(
          current,
          (resource) => resource.id === propertyFolderId,
          newResource,
        );
      },
      { revalidate: false },
    );
    return newResource;
  }

  return {
    propertyFolders,
    propertyFoldersById,
    error,
    isLoading,
    createPropertyFolder: create,
    updatePropertyFolder,
    deletePropertyFolder: del,
    propertyFolderToQlower,
    propertyFolderTransacToQlower,
    mutate,
  };
}
